import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  UploadSimple, FilePdf, MagnifyingGlass, ArrowClockwise, Trash,
  CircleNotch, WarningCircle, CheckCircle, Info, X, CaretDown, CaretRight,
  Sparkle, Hash, Copy, Check, Books,
} from "@phosphor-icons/react";
import KnowledgeV2DocDetail from "./KnowledgeV2DocDetail";

/*
 * KnowledgeV2Panel — UI Apple-Premium para a Base de Conhecimento v2.
 *
 * Endpoints consumidos (todos com tenant vindo do JWT):
 *   POST   /api/knowledge/v2/upload
 *   GET    /api/knowledge/v2
 *   GET    /api/knowledge/v2/{id}
 *   GET    /api/knowledge/v2/{id}/pages
 *   POST   /api/knowledge/v2/{id}/retry
 *   DELETE /api/knowledge/v2/{id}
 *   POST   /api/knowledge/v2/query
 *   POST   /api/knowledge/v2/query-global
 *
 * Regras críticas:
 *   • Polling REAL — só documentos em estados não-terminais são
 *     consultados. Um único timer global. Limpo ao desmontar.
 *   • Estados finais: ready, partially_processed, failed. Terminam polling.
 *   • Progresso REAL: "537 de 1.000 páginas processadas — 53,7%".
 *   • Sem tenant_id no request. Tenant vem exclusivamente do JWT.
 */

// ── State dictionary (PT-BR) ─────────────────────────────────────
const STATE_LABEL = {
  uploaded: "Enviado",
  queued: "Aguardando processamento",
  extracting: "Extraindo texto",
  running_ocr: "Executando OCR",
  chunking: "Criando blocos",
  generating_embeddings: "Gerando índice semântico",
  indexing: "Indexando",
  ready: "Pronto",
  partially_processed: "Concluído parcialmente",
  failed: "Falha",
};

const TERMINAL_STATES = new Set(["ready", "partially_processed", "failed"]);
const PROCESSING_STATES = new Set([
  "uploaded", "queued", "extracting", "running_ocr",
  "chunking", "generating_embeddings", "indexing",
]);

function stateBadge(state) {
  const label = STATE_LABEL[state] || state;
  const map = {
    ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partially_processed: "bg-amber-50 text-amber-700 border-amber-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
    queued: "bg-slate-100 text-slate-600 border-slate-200",
    uploaded: "bg-slate-100 text-slate-600 border-slate-200",
    extracting: "bg-sky-50 text-sky-700 border-sky-200",
    running_ocr: "bg-indigo-50 text-indigo-700 border-indigo-200",
    chunking: "bg-violet-50 text-violet-700 border-violet-200",
    generating_embeddings: "bg-violet-50 text-violet-700 border-violet-200",
    indexing: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return { label, cls: map[state] || "bg-slate-100 text-slate-600 border-slate-200" };
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Mapeia erros HTTP → mensagem PT-BR pra usuário final.
function humanError(err, fallback = "Ocorreu um erro. Tente novamente.") {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.length < 200) return detail;
  const map = {
    401: "Sua sessão expirou. Faça login novamente.",
    403: "Você não tem permissão para essa ação.",
    404: "Documento não encontrado.",
    409: "Este documento já existe na sua base.",
    413: "Arquivo maior que o limite permitido (200 MB).",
    422: "Arquivo inválido. Verifique se é um PDF válido.",
    429: "Muitas requisições. Aguarde alguns segundos e tente novamente.",
    500: "Erro interno no servidor. Nossa equipe já foi notificada.",
    503: "Serviço temporariamente indisponível. Tente novamente em instantes.",
  };
  return map[status] || fallback;
}

// ── Progress line (real progress) ────────────────────────────────
function ProgressLine({ doc }) {
  const total = doc.total_pages || 0;
  const done = doc.processed_pages || 0;
  const failed = doc.failed_pages_count || 0;
  const pct = total > 0 ? (done / total) * 100 : 0;
  const failedPct = total > 0 ? (failed / total) * 100 : 0;

  if (total === 0 && PROCESSING_STATES.has(doc.state)) {
    return (
      <div className="mt-2 flex items-center gap-2 text-[11.5px] text-slate-500">
        <CircleNotch size={11} className="animate-spin" />
        <span>{STATE_LABEL[doc.state] || doc.state}…</span>
      </div>
    );
  }
  if (total === 0) return null;

  return (
    <div className="mt-2" data-testid={`kb2-progress-${doc.id}`}>
      <div className="flex items-center justify-between text-[11.5px] text-slate-500 mb-1 tabular-nums">
        <span>
          <span className="font-medium text-slate-800">{done}</span>
          {" de "}
          <span className="font-medium text-slate-800">{total}</span>
          {" páginas processadas"}
          {failed > 0 && (
            <span className="text-rose-600 ml-1">
              · {failed} com erro
            </span>
          )}
        </span>
        <span className="tabular-nums">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
        <div
          className="h-full bg-slate-900 transition-all"
          style={{ width: `${pct - failedPct}%` }}
        />
        {failedPct > 0 && (
          <div
            className="h-full bg-rose-400 transition-all"
            style={{ width: `${failedPct}%` }}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────
export default function KnowledgeV2Panel({ onToast }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStage, setUploadStage] = useState("");   // "Enviando arquivo…"
  const [detailDocId, setDetailDocId] = useState(null);
  const [deleting, setDeleting] = useState({});         // {id: true}
  const [retrying, setRetrying] = useState({});         // {id: true}
  const fileRef = useRef(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // ── LOAD list ──────────────────────────────────────────────────
  const loadDocs = useCallback(async () => {
    try {
      const res = await api.get("/knowledge/v2");
      if (!mountedRef.current) return;
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      onToast?.(humanError(err, "Não foi possível carregar os documentos."), "error");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    mountedRef.current = true;
    loadDocs();
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [loadDocs]);

  // ── POLLING — só documentos ativos ─────────────────────────────
  const activeIds = useMemo(
    () => docs.filter((d) => !TERMINAL_STATES.has(d.state)).map((d) => d.id),
    [docs],
  );

  useEffect(() => {
    // Limpa qualquer timer antigo — evita múltiplos timers simultâneos.
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (activeIds.length === 0) return;

    const tick = async () => {
      // Buscar cada ativo em paralelo.
      try {
        const results = await Promise.all(
          activeIds.map((id) =>
            api.get(`/knowledge/v2/${id}`).then((r) => r.data).catch(() => null),
          ),
        );
        if (!mountedRef.current) return;
        setDocs((prev) => {
          const byId = Object.fromEntries(
            results.filter(Boolean).map((d) => [d.id, d]),
          );
          return prev.map((d) => byId[d.id] ? { ...d, ...byId[d.id] } : d);
        });
      } catch {
        // Não interrompe o timer por uma falha isolada; polling seguinte tenta de novo.
      }
    };

    // Executa imediatamente e depois a cada 2.5s.
    tick();
    pollTimerRef.current = setInterval(tick, 2500);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [activeIds.join(",")]); // depende dos ids ativos

  // ── UPLOAD ──────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      onToast?.("Por enquanto aceitamos apenas PDF.", "error");
      return;
    }
    setUploading(true);
    setUploadStage("Enviando arquivo…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/knowledge/v2/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data?.duplicate) {
        onToast?.("Este documento já está na sua Base de Conhecimento.", "info");
        await loadDocs();
        if (data.document_id) setDetailDocId(data.document_id);
      } else {
        onToast?.("PDF enviado. O processamento começou em segundo plano.");
        setUploadStage("Aguardando processamento…");
        await loadDocs();
      }
    } catch (err) {
      onToast?.(humanError(err, "Falha ao enviar o PDF."), "error");
    } finally {
      setUploading(false);
      setUploadStage("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // ── RETRY ───────────────────────────────────────────────────────
  const retryDoc = async (id) => {
    if (retrying[id]) return;
    setRetrying((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/knowledge/v2/${id}/retry`);
      onToast?.("Reprocessamento iniciado. Acompanhe o progresso.");
      await loadDocs();
    } catch (err) {
      onToast?.(humanError(err, "Falha ao reprocessar."), "error");
    } finally {
      setRetrying((p) => ({ ...p, [id]: false }));
    }
  };

  // ── DELETE ──────────────────────────────────────────────────────
  const deleteDoc = async (doc) => {
    if (deleting[doc.id]) return;
    const ok = window.confirm(
      `Remover "${doc.title}"?\n\nIsso apaga o documento, todas as páginas, o índice vetorial e as fontes de citação. Essa ação é irreversível.`,
    );
    if (!ok) return;
    setDeleting((p) => ({ ...p, [doc.id]: true }));
    try {
      await api.delete(`/knowledge/v2/${doc.id}`);
      // remove imediatamente da lista, evita polling em doc morto
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      onToast?.("Documento removido.");
    } catch (err) {
      onToast?.(humanError(err, "Falha ao remover documento."), "error");
    } finally {
      setDeleting((p) => ({ ...p, [doc.id]: false }));
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="kb2-panel">
      {/* Upload card */}
      <div
        className="rounded-2xl border border-slate-200 bg-white p-5"
        data-testid="kb2-upload-card"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
            <UploadSimple size={18} weight="regular" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-slate-900 tracking-tight">
              Envie um PDF para o Cérebro
            </div>
            <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
              Aceitamos PDFs de até 200 MB e 2.000 páginas. Documentos escaneados usam OCR automático (português + inglês).
              O processamento roda em segundo plano e você pode acompanhar aqui embaixo em tempo real.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <input
              type="file"
              ref={fileRef}
              accept="application/pdf,.pdf"
              className="hidden"
              data-testid="kb2-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="kb2-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <CircleNotch size={12} className="animate-spin" />
                  {uploadStage || "Enviando…"}
                </>
              ) : (
                <>
                  <FilePdf size={12} weight="bold" />
                  Selecionar PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Query console */}
      <QueryConsole docs={docs} onToast={onToast} />

      {/* List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[12px] text-slate-500">
            {loading ? "Carregando…" : (
              docs.length === 0 ? "Nenhum documento enviado ainda."
                : `${docs.length} documento${docs.length === 1 ? "" : "s"}`
            )}
          </div>
        </div>

        {loading ? null : docs.length === 0 ? (
          <div
            className="px-6 py-12 rounded-2xl border border-dashed border-slate-200 text-center"
            data-testid="kb2-empty"
          >
            <div className="mx-auto w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <Books size={18} weight="regular" className="text-slate-400" />
            </div>
            <p className="text-sm text-slate-600 font-medium">Base v2 vazia</p>
            <p className="text-[12px] text-slate-400 mt-1">
              Envie o primeiro PDF grande (manual, contrato, apostila…) e veja o AgentUS aprender por página.
            </p>
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="kb2-docs-list">
            {docs.map((doc) => (
              <DocRow
                key={doc.id}
                doc={doc}
                onOpen={() => setDetailDocId(doc.id)}
                onRetry={() => retryDoc(doc.id)}
                onDelete={() => deleteDoc(doc)}
                deleting={!!deleting[doc.id]}
                retrying={!!retrying[doc.id]}
              />
            ))}
          </ul>
        )}
      </div>

      {detailDocId && (
        <KnowledgeV2DocDetail
          docId={detailDocId}
          onClose={() => setDetailDocId(null)}
          onToast={onToast}
          onChange={loadDocs}
        />
      )}
    </div>
  );
}

// ── Doc row ──────────────────────────────────────────────────────
function DocRow({ doc, onOpen, onRetry, onDelete, deleting, retrying }) {
  const badge = stateBadge(doc.state);
  const isProcessing = PROCESSING_STATES.has(doc.state);
  const canRetry = doc.state === "partially_processed" || doc.state === "failed";
  const hasWarnings = (doc.warnings || []).length > 0;

  return (
    <li
      className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5"
      data-testid={`kb2-doc-${doc.id}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <FilePdf size={18} weight="regular" className="text-slate-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onOpen}
              className="text-[14px] font-medium text-slate-900 truncate hover:underline text-left"
              data-testid={`kb2-doc-title-${doc.id}`}
            >
              {doc.title}
            </button>
            <span
              className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${badge.cls}`}
              data-testid={`kb2-doc-state-${doc.id}`}
            >
              {isProcessing && <CircleNotch size={9} className="inline mr-1 animate-spin" />}
              {badge.label}
            </span>
            {doc.ocr_used && (
              <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border bg-indigo-50 text-indigo-700 border-indigo-200">
                OCR
              </span>
            )}
            {doc.document_version > 1 && (
              <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border bg-slate-100 text-slate-500 border-slate-200">
                v{doc.document_version}
              </span>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-slate-500 tabular-nums">
            <span>{formatBytes(doc.size_bytes)}</span>
            {doc.total_pages > 0 && <span>· {doc.total_pages} páginas</span>}
            {typeof doc.chunks_count === "number" && doc.chunks_count > 0 && (
              <span>· {doc.chunks_count} blocos</span>
            )}
            <span>· Enviado em {formatDateTime(doc.uploaded_at)}</span>
            {doc.processed_at && (
              <span>· Concluído em {formatDateTime(doc.processed_at)}</span>
            )}
          </div>

          <ProgressLine doc={doc} />

          {doc.error && (
            <div className="mt-2 flex items-start gap-2 text-[11.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              <WarningCircle size={13} weight="fill" className="shrink-0 mt-[1px]" />
              <span className="break-words">{doc.error}</span>
            </div>
          )}

          {hasWarnings && (
            <div className="mt-2 text-[11px] text-amber-700">
              {(doc.warnings || []).slice(0, 2).map((w, i) => (
                <div key={i}>· {w.message || w.code}</div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {canRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              data-testid={`kb2-doc-retry-${doc.id}`}
              title="Reprocessar páginas com falha"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {retrying ? (
                <CircleNotch size={11} className="animate-spin" />
              ) : (
                <ArrowClockwise size={11} weight="bold" />
              )}
              Reprocessar
            </button>
          )}
          <button
            type="button"
            onClick={onOpen}
            data-testid={`kb2-doc-open-${doc.id}`}
            title="Ver detalhes"
            className="inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 rounded-full text-[11px] font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          >
            <Info size={11} weight="regular" />
            <span className="hidden sm:inline">Detalhes</span>
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            data-testid={`kb2-doc-delete-${doc.id}`}
            title="Excluir documento"
            className="p-2 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-600 disabled:opacity-50"
          >
            {deleting ? <CircleNotch size={13} className="animate-spin" /> : <Trash size={13} />}
          </button>
        </div>
      </div>
    </li>
  );
}

// ── Query console ───────────────────────────────────────────────
function QueryConsole({ docs, onToast }) {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all"); // all | <docId>
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {results,is_global_query,global_notice}
  const [expanded, setExpanded] = useState({});
  const [globalRunning, setGlobalRunning] = useState(false);
  const [globalResult, setGlobalResult] = useState(null);

  const readyDocs = docs.filter(
    (d) => d.state === "ready" || d.state === "partially_processed",
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || running) return;
    setRunning(true);
    setGlobalResult(null);
    try {
      const body = { q: q.trim(), top_k: 8 };
      if (scope !== "all") body.document_id = scope;
      const { data } = await api.post("/knowledge/v2/query", body);
      // O backend já aplica o cutoff de baixa confiança (MIN_RELEVANCE_SCORE).
      // O frontend apenas exibe o que o orquestrador considerou evidência suficiente.
      setResult(data);
      setExpanded({});
    } catch (err) {
      onToast?.(humanError(err, "Falha na consulta."), "error");
    } finally {
      setRunning(false);
    }
  };

  const runGlobal = async () => {
    if (globalRunning) return;
    if (scope === "all") {
      onToast?.("Análise global exige um documento específico. Selecione um documento na consulta.", "error");
      return;
    }
    setGlobalRunning(true);
    try {
      const { data } = await api.post("/knowledge/v2/query-global", {
        q: q.trim(), document_id: scope,
      });
      setGlobalResult(data);
    } catch (err) {
      onToast?.(humanError(err, "Falha na análise global."), "error");
    } finally {
      setGlobalRunning(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="kb2-query-console">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
          <MagnifyingGlass size={16} weight="regular" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 tracking-tight">
            Consulte a Base de Conhecimento
          </div>
          <p className="text-[11.5px] text-slate-500 mt-0.5">
            Faça uma pergunta em linguagem natural. As respostas mostram o documento e as páginas exatas que embasaram cada trecho.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ex.: Qual o prazo de reembolso citado no manual?"
          data-testid="kb2-query-input"
          className="flex-1 px-4 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
        />
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          data-testid="kb2-query-scope"
          className="px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
        >
          <option value="all">Todos os documentos</option>
          {readyDocs.map((d) => (
            <option key={d.id} value={d.id}>{d.title}</option>
          ))}
        </select>
        <button
          type="submit"
          disabled={running || !q.trim() || readyDocs.length === 0}
          data-testid="kb2-query-submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] disabled:opacity-50"
        >
          {running ? <CircleNotch size={12} className="animate-spin" /> : <MagnifyingGlass size={12} weight="bold" />}
          {running ? "Consultando…" : "Consultar"}
        </button>
      </form>

      {readyDocs.length === 0 && (
        <p className="mt-3 text-[11.5px] text-slate-400">
          Envie um PDF acima e aguarde ficar em &quot;Pronto&quot; para começar a consultar.
        </p>
      )}

      {/* Global notice */}
      {result?.is_global_query && (
        <div
          data-testid="kb2-global-notice"
          className="mt-4 flex items-start gap-3 p-3.5 rounded-xl bg-indigo-50 border border-indigo-200"
        >
          <Sparkle size={14} weight="fill" className="text-indigo-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium text-indigo-900">
              Parece uma pergunta ampla sobre o documento inteiro
            </div>
            <p className="text-[11.5px] text-indigo-800 mt-0.5 leading-relaxed">
              A busca por trechos já traz os melhores resultados abaixo. Se quiser, também posso varrer o documento inteiro em modo análise (leva mais alguns segundos).
            </p>
            <button
              type="button"
              onClick={runGlobal}
              disabled={globalRunning || scope === "all"}
              data-testid="kb2-global-run"
              className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-900 hover:bg-indigo-800 text-white text-[11.5px] disabled:opacity-50"
              title={scope === "all" ? "Selecione um documento específico" : "Analisar documento inteiro"}
            >
              {globalRunning ? <CircleNotch size={11} className="animate-spin" /> : <Sparkle size={11} weight="bold" />}
              {globalRunning ? "Analisando…" : "Analisar documento inteiro"}
            </button>
          </div>
        </div>
      )}

      {/* Global result */}
      {globalResult && (
        <div
          data-testid="kb2-global-result"
          className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4"
        >
          <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-800 mb-2">
            <Sparkle size={12} weight="fill" className="text-indigo-700" />
            Análise completa
            {globalResult.cached && (
              <span className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border bg-slate-100 text-slate-500 border-slate-200">
                cache
              </span>
            )}
          </div>
          {globalResult.ok === false ? (
            <p className="text-[12px] text-rose-700">
              {globalResult.message || "Não foi possível gerar a análise global agora."}
            </p>
          ) : (
            <>
              <p className="text-[12.5px] text-slate-800 whitespace-pre-wrap leading-relaxed">
                {globalResult.answer || "Sem resposta."}
              </p>
              {Array.isArray(globalResult.pages_covered) && globalResult.pages_covered.length > 0 && (
                <p className="mt-2 text-[11px] text-slate-500">
                  Páginas percorridas: {globalResult.pages_covered.join(", ")}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Local search results */}
      {result && !globalResult && (
        <div className="mt-4" data-testid="kb2-query-results">
          {result.results?.length === 0 ? (
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-center">
              <p className="text-[13px] font-medium text-slate-700">
                Não encontrei essa informação nos documentos selecionados.
              </p>
              <p className="text-[11.5px] text-slate-500 mt-1">
                Tente reformular a pergunta ou envie um novo documento com o conteúdo esperado.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {result.results.map((r) => {
                const isOpen = !!expanded[r.chunk_id];
                return (
                  <li
                    key={r.chunk_id}
                    className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                    data-testid={`kb2-result-${r.chunk_id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded((p) => ({ ...p, [r.chunk_id]: !isOpen }))}
                      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50"
                    >
                      <div className="mt-1 shrink-0">
                        {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium text-slate-900">
                          Fonte: {r.citation}
                        </div>
                        {r.section_title && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Seção: {r.section_title}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 tabular-nums mt-0.5">
                          Relevância {(r.score * 100).toFixed(0)}%
                          {Array.isArray(r.sources) && r.sources.length > 0 && (
                            <> · {r.sources.join(" + ")}</>
                          )}
                        </div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                        <p className="text-[12.5px] text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {r.text_preview}
                          {r.text_preview?.length >= 400 && "…"}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
